import { useCallback, useEffect, useRef, useState } from 'react';
import { analyticsService } from '../services/analyticsService';
import { createBillingPortalSession } from '../transport/api/protectedApi';
import type { AnalyticsEventParams } from '../types/analytics';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('useUserStripePortal');

interface UseUserStripePortalOptions {
  returnUrl: string;
  autoFetch?: boolean;
  trackingLabel?: string;
  trackingContext?: Partial<AnalyticsEventParams>;
  onError?: (error: unknown) => void;
}

interface UseUserStripePortalResult {
  portalUrl: string | null;
  loadingPortal: boolean;
  portalError: string | null;
  fetchPortalUrl: () => Promise<string | null>;
  trackPortalClick: (portalUrlOverride?: string) => void;
  openPortal: () => Promise<boolean>;
}

export function useUserStripePortal({
  returnUrl,
  autoFetch = false,
  trackingLabel = 'Stripe Customer Portal Visit',
  trackingContext,
  onError,
}: UseUserStripePortalOptions): UseUserStripePortalResult {
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [loadingPortal, setLoadingPortal] = useState<boolean>(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const inFlightRequestRef = useRef<Promise<string | null> | null>(null);

  const fetchPortalUrl = useCallback(async (): Promise<string | null> => {
    if (inFlightRequestRef.current) {
      return inFlightRequestRef.current;
    }

    const request = (async () => {
      setLoadingPortal(true);
      setPortalError(null);

      try {
        const response = await createBillingPortalSession(returnUrl);
        if (response.success && response.data?.url) {
          setPortalUrl(response.data.url);
          return response.data.url;
        }

        throw new Error('Missing billing portal URL');
      } catch (error) {
        logger.error('Failed to create Stripe Customer Portal session', error);
        setPortalUrl(null);
        setPortalError('Stripe Customer Portal is unavailable right now.');
        onError?.(error);
        return null;
      } finally {
        setLoadingPortal(false);
        inFlightRequestRef.current = null;
      }
    })();

    inFlightRequestRef.current = request;
    return request;
  }, [onError, returnUrl]);

  useEffect(() => {
    setPortalUrl(null);
    setPortalError(null);
  }, [returnUrl]);

  useEffect(() => {
    if (autoFetch && !portalUrl && !loadingPortal) {
      void fetchPortalUrl();
    }
  }, [autoFetch, fetchPortalUrl, loadingPortal, portalUrl]);

  const trackPortalClick = useCallback(
    (portalUrlOverride?: string) => {
      const navigationTarget = portalUrlOverride ?? portalUrl;
      if (!navigationTarget) {
        return;
      }

      analyticsService.trackButtonClick(trackingLabel, {
        navigation_target: navigationTarget,
        link_type: 'external',
        ...trackingContext,
      });
    },
    [portalUrl, trackingContext, trackingLabel]
  );

  const openPortal = useCallback(async (): Promise<boolean> => {
    const nextPortalUrl = portalUrl ?? (await fetchPortalUrl());
    if (!nextPortalUrl) {
      return false;
    }

    trackPortalClick(nextPortalUrl);
    window.open(nextPortalUrl, '_blank', 'noopener,noreferrer');
    return true;
  }, [fetchPortalUrl, portalUrl, trackPortalClick]);

  return {
    portalUrl,
    loadingPortal,
    portalError,
    fetchPortalUrl,
    trackPortalClick,
    openPortal,
  };
}
